import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import ColumnVisibilityToggle from '../../../community/residents/components/ColumnVisibilityToggle.jsx';
import FilterPopover from '../../../community/residents/components/FilterPopover.jsx';
import ActiveFilterBar from '../../../community/residents/components/ActiveFilterBar.jsx';
import { exportPendantProfilesCsv } from '../../../../services/csvExport.js';
import usePersistedColumns from '../../../../hooks/usePersistedColumns.js';
import usePersistedPerPage from '../../../../hooks/usePersistedPerPage.js';

// ---------------------------------------------------------------------------
// Column definitions
// ---------------------------------------------------------------------------
const COLUMN_DEFS = [
    { key: 'profile_name', label: 'Profile Name', sortable: true, filterable: false, required: true },
];

// ---------------------------------------------------------------------------
// Row-level kebab menu
// ---------------------------------------------------------------------------
const RowActions = ({ row, onEdit, onDelete }) => {
    const [open, setOpen] = useState(false);
    const [menuStyle, setMenuStyle] = useState({});
    const menuRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (menuRef.current && !menuRef.current.contains(e.target)) setOpen(false);
        };
        if (open) document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [open]);

    useEffect(() => {
        if (!open) return;
        const onScroll = () => setOpen(false);
        window.addEventListener('scroll', onScroll, true);
        return () => window.removeEventListener('scroll', onScroll, true);
    }, [open]);

    const handleToggle = () => {
        if (!open && menuRef.current) {
            const rect = menuRef.current.getBoundingClientRect();
            const spaceBelow = window.innerHeight - rect.bottom;
            if (spaceBelow < 120) {
                setMenuStyle({ position: 'fixed', bottom: `${window.innerHeight - rect.top + 4}px`, right: `${window.innerWidth - rect.right}px`, top: 'auto', left: 'auto', margin: 0, zIndex: 9999 });
            } else {
                setMenuStyle({ position: 'fixed', top: `${rect.bottom + 4}px`, right: `${window.innerWidth - rect.right}px`, bottom: 'auto', left: 'auto', margin: 0, zIndex: 9999 });
            }
        }
        setOpen(!open);
    };

    return (
        <div className="ap-row-actions" ref={menuRef}>
            <button type="button" onClick={handleToggle} className="ap-row-actions__trigger" aria-label={`Actions for ${row.profile_name}`} aria-expanded={open}>
                <i className="fe fe-more-vertical" aria-hidden="true" />
            </button>
            {open ? (
                <div className="ap-row-actions__menu" style={menuStyle}>
                    <button type="button" onClick={() => { onEdit(row); setOpen(false); }} className="ap-row-actions__item">
                        <svg className="ap-icon-md ap-row-actions__item-icon" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10" />
                        </svg>
                        Edit Profile
                    </button>
                    <button type="button" onClick={() => { onDelete([row.dsp_profile_id]); setOpen(false); }} className="ap-row-actions__item ap-row-actions__item--danger">
                        <svg className="ap-icon-md ap-row-actions__item-icon" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
                        </svg>
                        Delete Profile
                    </button>
                </div>
            ) : null}
        </div>
    );
};

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------
const ALL_COLUMN_KEYS = COLUMN_DEFS.map((c) => c.key);

const ProfileTable = ({ profiles, onEdit, onDelete, onBulkDelete, saving }) => {
    const DEFAULT_VISIBLE = useMemo(() => COLUMN_DEFS.map((c) => c.key), []);
    const COLUMN_LABELS = useMemo(() => {
        const labels = {};
        COLUMN_DEFS.forEach((col) => { labels[col.key] = col.label; });
        return labels;
    }, []);

    const [search, setSearch] = useState('');
    const [sortColumn, setSortColumn] = useState('profile_name');
    const [sortOrder, setSortOrder] = useState('asc');
    const [filters, setFilters] = useState({});
    const [visibleColumns, handleColumnToggle] = usePersistedColumns('settings-pendant-profiles', DEFAULT_VISIBLE, ALL_COLUMN_KEYS);
    const [page, setPage] = useState(1);
    const [perPage, setPerPage] = usePersistedPerPage();
    const [selectedIds, setSelectedIds] = useState([]);

    // Reset state when profiles change
    useEffect(() => {
        setSearch('');
        setFilters({});
        setSortColumn('profile_name');
        setSortOrder('asc');
        setPage(1);
        setSelectedIds([]);
    }, [profiles]);

    // Active columns
    const activeColumns = useMemo(
        () => COLUMN_DEFS.filter((col) => visibleColumns.includes(col.key)),
        [visibleColumns]
    );

    // Filter values
    const getFilterValues = useCallback((columnKey) => {
        const values = new Set();
        (profiles || []).forEach((row) => {
            const val = row[columnKey];
            values.add(val != null && val !== '' ? String(val) : '(empty)');
        });
        return [...values].sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }));
    }, [profiles]);

    // ======================================================================
    // Data pipeline: Search -> Filters -> Sort
    // ======================================================================
    const filteredData = useMemo(() => {
        let d = [...(profiles || [])];

        if (search.trim()) {
            const term = search.toLowerCase();
            d = d.filter((r) =>
                (r.profile_name || '').toLowerCase().includes(term)
            );
        }

        Object.entries(filters).forEach(([columnKey, values]) => {
            if (!values || values.length === 0) return;
            d = d.filter((row) => {
                const val = row[columnKey];
                const strVal = val != null && val !== '' ? String(val) : '(empty)';
                return values.includes(strVal);
            });
        });

        d.sort((a, b) => {
            const aVal = a[sortColumn] ?? '';
            const bVal = b[sortColumn] ?? '';
            const result = typeof aVal === 'number' && typeof bVal === 'number'
                ? aVal - bVal
                : String(aVal).localeCompare(String(bVal), undefined, { numeric: true, sensitivity: 'base' });
            return sortOrder === 'asc' ? result : -result;
        });

        return d;
    }, [profiles, search, filters, sortColumn, sortOrder]);

    useEffect(() => { setPage(1); }, [search, filters, sortColumn, sortOrder]);

    // ======================================================================
    // Pagination
    // ======================================================================
    const totalFiltered = filteredData.length;
    const totalPages = Math.max(1, Math.ceil(totalFiltered / perPage));
    const safePage = Math.min(page, totalPages);
    const startIdx = (safePage - 1) * perPage;
    const endIdx = Math.min(startIdx + perPage, totalFiltered);
    const paginatedData = filteredData.slice(startIdx, endIdx);

    const showingStart = totalFiltered === 0 ? 0 : startIdx + 1;
    const showingEnd = endIdx;

    const pageNumbers = useMemo(() => {
        const pages = [];
        const maxVisible = 5;
        let start = Math.max(1, safePage - Math.floor(maxVisible / 2));
        let end = Math.min(totalPages, start + maxVisible - 1);
        if (end - start + 1 < maxVisible) start = Math.max(1, end - maxVisible + 1);
        for (let i = start; i <= end; i++) pages.push(i);
        return pages;
    }, [safePage, totalPages]);

    // ======================================================================
    // Selection (bulk delete)
    // ======================================================================
    const allPageSelected = paginatedData.length > 0 && paginatedData.every((r) => selectedIds.includes(r.dsp_profile_id));
    const somePageSelected = selectedIds.length > 0 && !allPageSelected;

    const handleSelectAll = () => {
        const pageIds = paginatedData.map((r) => r.dsp_profile_id);
        if (allPageSelected) {
            setSelectedIds((prev) => prev.filter((id) => !pageIds.includes(id)));
        } else {
            setSelectedIds((prev) => [...new Set([...prev, ...pageIds])]);
        }
    };

    const handleRowSelect = (id) => {
        setSelectedIds((prev) =>
            prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
        );
    };

    // ======================================================================
    // Handlers
    // ======================================================================
    const handleSort = useCallback((column) => {
        if (sortColumn === column) {
            setSortOrder((o) => (o === 'asc' ? 'desc' : 'asc'));
        } else {
            setSortColumn(column);
            setSortOrder('asc');
        }
    }, [sortColumn]);

    const handleFilterChange = useCallback((columnKey, values) => {
        setFilters((prev) => ({ ...prev, [columnKey]: values }));
    }, []);

    const handleRemoveFilter = useCallback((columnKey, value) => {
        setFilters((prev) => ({ ...prev, [columnKey]: (prev[columnKey] || []).filter((v) => v !== value) }));
    }, []);

    const handleClearAllFilters = useCallback(() => { setFilters({}); setSearch(''); }, []);

    const handleExportAll = useCallback(() => {
        if (filteredData.length) exportPendantProfilesCsv(filteredData);
    }, [filteredData]);

    const handleBulkDelete = () => {
        if (selectedIds.length > 0) {
            onBulkDelete(selectedIds);
        }
    };

    // ======================================================================
    // Sort icons
    // ======================================================================
    const renderSortIcon = (columnKey) => {
        if (sortColumn !== columnKey) {
            return (
                <svg className="ap-icon-sm ap-sort-icon ap-sort-icon--neutral" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 15L12 18.75 15.75 15m-7.5-6L12 5.25 15.75 9" />
                </svg>
            );
        }
        return sortOrder === 'asc' ? (
            <svg className="ap-icon-sm ap-sort-icon ap-sort-icon--active" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 15.75l7.5-7.5 7.5 7.5" />
            </svg>
        ) : (
            <svg className="ap-icon-sm ap-sort-icon ap-sort-icon--active" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
            </svg>
        );
    };

    // ======================================================================
    // Cell rendering
    // ======================================================================
    const renderCell = (row, columnKey) => {
        return <span className="ap-cell-text">{row[columnKey] != null ? row[columnKey] : '\u2014'}</span>;
    };

    // ======================================================================
    // Render
    // ======================================================================
    const hasFilters = search.trim() || Object.values(filters).some((v) => v.length > 0);

    return (
        <>
            {/* Controls row */}
            <div className="ap-controls">
                <div className="ap-controls__left">
                    <div className="ap-search">
                        <svg className="ap-icon-md ap-search__icon" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
                        </svg>
                        <input type="search" placeholder="Search profiles..." value={search} onChange={(e) => setSearch(e.target.value)} className="ap-search__input" aria-label="Search profiles" />
                    </div>
                    {selectedIds.length > 0 ? (
                        <button type="button" className="dp-btn dp-btn--danger-outline" onClick={handleBulkDelete} style={{ marginLeft: 'var(--space-2, 0.5rem)' }}>
                            <i className="fe fe-trash-2" style={{ marginRight: '0.35rem' }} />
                            Delete ({selectedIds.length})
                        </button>
                    ) : null}
                </div>
                <div className="ap-controls__right">
                    <ColumnVisibilityToggle columns={COLUMN_DEFS} visibleColumns={visibleColumns} onToggle={handleColumnToggle} />
                    <button type="button" className="ap-btn ap-btn--secondary" onClick={handleExportAll} aria-label="Export profiles">
                        <svg className="ap-icon-md" style={{ color: 'var(--color-text-secondary)' }} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" />
                        </svg>
                        Export
                    </button>
                </div>
            </div>

            {/* Active filter chips */}
            <ActiveFilterBar filters={filters} columnLabels={COLUMN_LABELS} onRemoveFilter={handleRemoveFilter} onClearAll={handleClearAllFilters} />

            {/* Table */}
            {filteredData.length === 0 ? (
                <div className="ss-empty" style={{ marginTop: 'var(--space-4)' }}>
                    <p className="ss-empty__text">
                        {hasFilters ? 'No profiles match your filters.' : 'No profiles configured.'}
                    </p>
                </div>
            ) : (
                <>
                    <div className="ap-table-wrap">
                        <table className="ap-table">
                            <thead className="ap-thead">
                                <tr>
                                    <th scope="col" className="ap-th ap-th--checkbox">
                                        <input
                                            type="checkbox"
                                            className="ap-checkbox"
                                            checked={allPageSelected}
                                            ref={(el) => { if (el) el.indeterminate = somePageSelected; }}
                                            onChange={handleSelectAll}
                                            aria-label="Select all profiles on this page"
                                        />
                                    </th>
                                    <th scope="col" className="ap-th ap-th--actions">Edit</th>
                                    {activeColumns.map((col) => (
                                        <th key={col.key} scope="col" className="ap-th">
                                            <div className="ap-th__content">
                                                {col.sortable ? (
                                                    <button type="button" onClick={() => handleSort(col.key)} className="ap-th__sort-btn" aria-label={`Sort by ${col.label}`}>
                                                        {col.label}
                                                        {renderSortIcon(col.key)}
                                                    </button>
                                                ) : <span>{col.label}</span>}
                                                {col.filterable ? (
                                                    <FilterPopover columnKey={col.key} label={col.label} values={getFilterValues(col.key)} selectedValues={filters[col.key] || []} onChange={handleFilterChange} />
                                                ) : null}
                                            </div>
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody className="ap-tbody">
                                {paginatedData.map((row) => {
                                    const isSelected = selectedIds.includes(row.dsp_profile_id);
                                    return (
                                        <tr key={row.dsp_profile_id} className={`ap-tr${isSelected ? ' is-selected' : ''}`}>
                                            <td className="ap-td ap-td--checkbox">
                                                <input
                                                    type="checkbox"
                                                    className="ap-checkbox"
                                                    checked={isSelected}
                                                    onChange={() => handleRowSelect(row.dsp_profile_id)}
                                                    aria-label={`Select ${row.profile_name}`}
                                                />
                                            </td>
                                            <td className="ap-td ap-td--actions">
                                                <RowActions row={row} onEdit={onEdit} onDelete={onDelete} />
                                            </td>
                                            {activeColumns.map((col) => (
                                                <td key={col.key} className="ap-td">
                                                    {renderCell(row, col.key)}
                                                </td>
                                            ))}
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination */}
                    <div className="ap-pagination">
                        <p className="ap-pagination__info">
                            Showing <span className="ap-pagination__emphasis">{showingStart}</span>&ndash;<span className="ap-pagination__emphasis">{showingEnd}</span> of{' '}
                            <span className="ap-pagination__emphasis">{totalFiltered}</span> profile{totalFiltered !== 1 ? 's' : ''}
                        </p>
                        <div className="ap-pagination__controls">
                            <div className="ap-per-page">
                                <label htmlFor="pp-per-page" className="ap-per-page__label">Rows per page:</label>
                                <select id="pp-per-page" value={perPage} onChange={(e) => { setPerPage(parseInt(e.target.value, 10)); setPage(1); }} className="ap-per-page__select">
                                    <option value={25}>25</option>
                                    <option value={50}>50</option>
                                    <option value={100}>100</option>
                                    <option value={200}>200</option>
                                    <option value={300}>300</option>
                                </select>
                            </div>
                            <nav className="ap-page-nav" aria-label="Profile pagination">
                                <button type="button" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={safePage <= 1} className="ap-page-btn" aria-label="Previous page">
                                    <svg className="ap-icon-md" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
                                    </svg>
                                </button>
                                {pageNumbers.map((num) => (
                                    <button key={num} type="button" onClick={() => setPage(num)} className={`ap-page-num${num === safePage ? ' is-active' : ''}`} aria-label={`Page ${num}`} aria-current={num === safePage ? 'page' : undefined}>
                                        {num}
                                    </button>
                                ))}
                                <button type="button" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={safePage >= totalPages} className="ap-page-btn" aria-label="Next page">
                                    <svg className="ap-icon-md" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
                                    </svg>
                                </button>
                            </nav>
                        </div>
                    </div>
                </>
            )}
        </>
    );
};

export default ProfileTable;
